import { useContext, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PosContext } from "../../../shared/PosContext";

export default function RetailSaleNotesDialog({
    order,
    customer,
    total,
    onClose,
    onSave,
    loading,
}) {
    const { t } = useTranslation();
    const { customers = [] } = useContext(PosContext);
    const [form, setForm] = useState(() =>
        Object.fromEntries(
            [
                "tracking_number",
                "delivery_charge",
                "delivery_discount",
                "notes",
                "customer_details",
                "mobile_no",
                "address_line1",
                "address_line2",
                "customer",
            ].map((key) => [
                key,
                order?.[key] ?? (key === "customer" ? customer || "" : ""),
            ])
        )
    );
    const change = (key, value) =>
        setForm((previous) => ({ ...previous, [key]: value }));
    const charge = Number(form.delivery_charge || 0);
    const discount = Number(form.delivery_discount || 0);
    const invalid =
        !Number.isFinite(charge) ||
        !Number.isFinite(discount) ||
        charge < 0 ||
        discount < 0 ||
        discount > charge;
    const field = (key, label, numeric = false, multiline = false) => (
        <TextField
            key={key}
            label={t(`retailPos.${label}`)}
            value={form[key]}
            fullWidth
            onChange={(event) => change(key, event.target.value)}
            disabled={loading}
            type={numeric ? "number" : "text"}
            multiline={multiline}
            minRows={multiline ? 2 : undefined}
            inputProps={numeric ? { min: 0, step: "0.01" } : undefined}
        />
    );
    return (
        <Dialog
            open
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{t("retailPos.saveWithNotes")}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
                    {field("tracking_number", "trackingNumber")}
                    <Box sx={{ display: "flex", gap: 2 }}>
                        {field("delivery_charge", "deliveryCharge", true)}
                        {field("delivery_discount", "deliveryDiscount", true)}
                    </Box>
                    {invalid && (
                        <Alert severity="error">
                            {t("retailPos.invalidDelivery")}
                        </Alert>
                    )}
                    <Autocomplete
                        freeSolo
                        options={customers}
                        disabled={loading}
                        value={
                            customers.find(
                                (entry) => entry.name === form.customer
                            ) || form.customer
                        }
                        getOptionLabel={(entry) =>
                            typeof entry === "string"
                                ? entry
                                : entry.customer_name || entry.name
                        }
                        onChange={(_, entry) =>
                            change(
                                "customer",
                                typeof entry === "string"
                                    ? entry
                                    : entry?.name || ""
                            )
                        }
                        onInputChange={(_, value, reason) => {
                            if (reason === "input" || reason === "clear")
                                change("customer", value);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("retailPos.customer")}
                            />
                        )}
                    />
                    {field("mobile_no", "mobileNumber")}
                    {field("address_line1", "addressLine1")}
                    {field("address_line2", "addressLine2")}
                    {field("customer_details", "customerDetails", false, true)}
                    {field("notes", "notes", false, true)}
                    <Alert severity="info">
                        {t("retailPos.orderPlusDelivery")}:{" "}
                        {invalid
                            ? "—"
                            : (Number(total || 0) + charge - discount).toFixed(
                                  2
                              )}
                        <Box>{t("retailPos.deliveryInformationOnly")}</Box>
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {t("common.cancel")}
                </Button>
                <Button
                    variant="contained"
                    disabled={loading || invalid}
                    onClick={() =>
                        onSave({
                            ...form,
                            delivery_charge: charge,
                            delivery_discount: discount,
                        })
                    }
                >
                    {t(loading ? "common.saving" : "common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
